/**
 * DispatchPanel — herbruikbare "Chauffeur / Dispatch" card voor pickup &
 * delivery detail. Gebruikt:
 *   - GET /api/admin/drivers (lijst voor dropdown)
 *   - POST /api/admin/appointments/{id}/dispatch-preview
 *   - POST /api/admin/appointments/{id}/dispatch-whatsapp-link
 *   - POST /api/admin/appointments/{id}/dispatch-email
 *   - GET  /api/admin/appointments/{id}/dispatches (historie)
 *
 * Vereist `dispatch:send` permissie (zichtbaarheid wordt door parent gegate).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../ui/dialog";
import { toast } from "sonner";
import {
  Truck, MessageCircle, Send, Copy, AlertTriangle, History, RefreshCw,
  ExternalLink, CheckCheck,
} from "lucide-react";

const NAV_LABELS = { google: "Google Maps", waze: "Waze", apple: "Apple Maps" };

// Statuslabels voor dispatchgeschiedenis. We claimen GEEN "verzonden" bij
// WhatsApp-links omdat we de daadwerkelijke verzending niet kunnen bevestigen
// zonder WhatsApp Cloud API.
const DISPATCH_STATUS_LABELS = {
  whatsapp_link_generated: "WhatsApp-link gegenereerd",
  sent_manual: "Handmatig verzonden",
  sent: "Verzonden",
  failed: "Mislukt",
  prepared: "Voorbereid",
};
const DISPATCH_STATUS_CLASSES = {
  whatsapp_link_generated: "bg-emerald-100 text-emerald-700",
  sent_manual: "bg-green-100 text-green-800",
  sent: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  prepared: "bg-gray-100 text-gray-700",
};

// Synchrone HTML voor de placeholder-tab die we openen vóór de async API-call.
// iOS Safari + popup-blockers vereisen dat ``window.open`` direct binnen de
// click-event-stack wordt aangeroepen. Daarna pas mogen we (na await) de
// location naar de daadwerkelijke wa.me link zetten.
const PLACEHOLDER_HTML = `<!doctype html>
<meta charset="utf-8">
<title>WhatsApp openen…</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Arial;
       background:#0A192F;color:#fff;display:flex;align-items:center;
       justify-content:center;height:100vh;margin:0;font-size:14px;text-align:center;padding:24px}
  .box{max-width:340px}
  h1{font-size:18px;margin:0 0 10px;color:#25D366}
</style>
<div class="box">
  <h1>WhatsApp wordt geopend…</h1>
  <p>Een moment geduld. Als WhatsApp niet automatisch opent, sluit u dit tabblad af en gebruikt u de fallback-knop in het backoffice.</p>
</div>`;

export default function DispatchPanel({ appointment, kind = "pickup" }) {
  const { hasPermission } = useAuth();
  const canDispatch = hasPermission("dispatch:send");

  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [preview, setPreview] = useState(null);
  const [extraNote, setExtraNote] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Fallback-modal state — gevuld wanneer popup-blocking voorkomt of medewerker
  // het bericht/de link handmatig wil ophalen.
  const [fallback, setFallback] = useState(null);
  // "Markeer als verzonden"-bevestigingsmodal — gevuld met het dispatch-record
  // dat de medewerker wil bevestigen. We tonen deze knop alleen bij records
  // met channel=whatsapp_link en status=whatsapp_link_generated.
  const [markCandidate, setMarkCandidate] = useState(null);
  const [marking, setMarking] = useState(false);

  const selectedDriver = useMemo(
    () => drivers.find((d) => d.id === selectedDriverId) || null,
    [drivers, selectedDriverId],
  );

  // Laad actieve chauffeurs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get("/admin/drivers", { params: { active: true } });
        if (!cancelled) setDrivers(r.data);
      } catch (e) { if (!cancelled) toast.error(formatApiError(e)); }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadHistory = useCallback(async () => {
    if (!appointment?.id) return;
    setHistoryLoading(true);
    try {
      const r = await adminApi.get(`/admin/appointments/${appointment.id}/dispatches`);
      setHistory(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setHistoryLoading(false); }
  }, [appointment?.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Refresh preview wanneer chauffeur of extra-note wijzigt
  useEffect(() => {
    if (!selectedDriverId || !appointment?.id) { setPreview(null); return; }
    let cancelled = false;
    setLoadingPreview(true);
    (async () => {
      try {
        const r = await adminApi.post(
          `/admin/appointments/${appointment.id}/dispatch-preview`,
          { driver_id: selectedDriverId, kind },
        );
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        if (!cancelled) { setPreview(null); toast.error(formatApiError(e)); }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDriverId, appointment?.id, kind]);

  const onSendWhatsapp = () => {
    if (!selectedDriverId) { toast.error("Kies eerst een chauffeur."); return; }
    // ----------------------------------------------------------------
    // P0 popup-blocking fix:
    //   1) Open SYNCHROON een placeholder-tab in de click-event-stack.
    //      iOS Safari + Chrome/Firefox/Edge popup-blockers staan dit toe
    //      omdat het direct uit een user-gesture komt.
    //   2) Daarna pas de async API-call.
    //   3) Bij succes → herleid het placeholder-tab naar de wa.me URL.
    //   4) Bij block (null) → fallback-modal.
    //   5) Bij API-fout → sluit het placeholder-tab.
    // ----------------------------------------------------------------
    let whatsappWindow = null;
    try {
      whatsappWindow = window.open("about:blank", "_blank");
      if (whatsappWindow && whatsappWindow.document) {
        try { whatsappWindow.document.write(PLACEHOLDER_HTML); } catch (_) { /* iOS strict mode */ }
      }
    } catch (_) {
      whatsappWindow = null;
    }

    setSending(true);
    (async () => {
      try {
        const r = await adminApi.post(
          `/admin/appointments/${appointment.id}/dispatch-whatsapp-link`,
          { driver_id: selectedDriverId, kind, extra_note: extraNote || undefined },
        );
        const url = r.data?.whatsapp_url || r.data?.whatsapp_link;
        const messageText = r.data?.message_text || r.data?.dispatch?.message || "";
        if (!url) {
          if (whatsappWindow) { try { whatsappWindow.close(); } catch (_) { /* window al gesloten */ } }
          toast.error("Kon WhatsApp-link niet genereren.");
          return;
        }
        if (whatsappWindow && !whatsappWindow.closed) {
          // ---- Happy path: placeholder-tab leeft nog → herleid hem ----
          try {
            whatsappWindow.location.href = url;
            toast.success("WhatsApp geopend — verzending bevestigen in WhatsApp.");
          } catch (_) {
            // Cross-origin / detected closed na write → fallback.
            setFallback({ url, messageText });
          }
        } else {
          // ---- Popup geblokkeerd of tab gesloten → fallback-modal ----
          setFallback({ url, messageText });
        }
        loadHistory();
      } catch (e) {
        if (whatsappWindow) { try { whatsappWindow.close(); } catch (_) { /* window al gesloten */ } }
        toast.error(formatApiError(e));
      } finally {
        setSending(false);
      }
    })();
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} gekopieerd.`);
    } catch {
      toast.error("Kopiëren mislukt — selecteer de tekst handmatig.");
    }
  };

  const onConfirmMarkSent = async () => {
    if (!markCandidate) return;
    setMarking(true);
    try {
      await adminApi.post(`/admin/dispatches/${markCandidate.id}/mark-sent-manual`);
      toast.success("WhatsApp-dispatch gemarkeerd als verzonden.");
      setMarkCandidate(null);
      loadHistory();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMarking(false); }
  };

  const onConfirmSendEmail = async () => {
    setSending(true);
    try {
      const r = await adminApi.post(
        `/admin/appointments/${appointment.id}/dispatch-email`,
        { driver_id: selectedDriverId, kind, extra_note: extraNote || undefined },
      );
      if (r.data?.success) {
        toast.success("E-mail verzonden naar chauffeur.");
      } else {
        toast.warning("E-mail kon niet verstuurd worden (zie geschiedenis).");
      }
      setEmailConfirmOpen(false);
      loadHistory();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const copyNavLink = (which) => {
    const url = preview?.navigation_links?.[which];
    if (!url) return;
    copyText(url, `${NAV_LABELS[which]}-link`);
  };

  if (!canDispatch) return null;

  const kindLabel = kind === "delivery" ? "Delivery" : "Pickup";

  return (
    <section className="bg-white border border-gray-200 p-4 sm:p-6" data-testid="dispatch-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-[#0055FF]" />
          <h2 className="font-chivo font-bold text-base text-[#0A192F]">Chauffeur / Dispatch</h2>
          <span className="inline-block bg-blue-100 text-blue-700 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold">{kindLabel}</span>
        </div>
        <Button variant="outline" size="sm" onClick={loadHistory} className="rounded-none" data-testid="dispatch-refresh-history">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Vernieuwen
        </Button>
      </div>

      <div className="space-y-3">
        <div className="min-w-0">
          <Label className="text-xs uppercase tracking-wide font-bold">Chauffeur</Label>
          <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
            <SelectTrigger className="rounded-none mt-1 w-full" data-testid="dispatch-driver-select">
              <SelectValue placeholder={drivers.length === 0 ? "Geen actieve chauffeurs — voeg toe via Instellingen" : "Kies een chauffeur"} />
            </SelectTrigger>
            <SelectContent>
              {drivers.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name} {d.preferred_channel === "email" ? "· e-mail" : "· WhatsApp"} {d.default_region ? `· ${d.default_region}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedDriver && (
          <div className="text-[11px] text-gray-500 space-x-3 flex flex-wrap gap-y-1">
            {selectedDriver.phone_e164 && <span>Tel: {selectedDriver.phone_e164}</span>}
            {selectedDriver.whatsapp_e164 && <span>WhatsApp: {selectedDriver.whatsapp_e164}</span>}
            {selectedDriver.email && <span>E-mail: {selectedDriver.email}</span>}
            {selectedDriver.vehicle_plate && <span>{selectedDriver.vehicle_plate}</span>}
          </div>
        )}

        {preview?.address_incomplete && (
          <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 p-3 text-xs text-orange-800" data-testid="dispatch-address-warning">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Adres is incompleet</strong> — de navigatielink kan mogelijk niet correct werken.
              Ontbrekend: {(preview.missing_address_fields || []).join(", ") || "—"}
            </div>
          </div>
        )}

        {preview && (
          <>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Berichtpreview</Label>
              <Textarea
                rows={9}
                readOnly
                data-testid="dispatch-message-preview"
                value={preview.message || ""}
                className="rounded-none mt-1 w-full min-w-0 font-mono text-[11px]"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Extra notitie (optioneel)</Label>
              <Textarea
                rows={2}
                data-testid="dispatch-extra-note"
                value={extraNote}
                onChange={(e) => setExtraNote(e.target.value)}
                placeholder="Bijv. 'Niet bellen vóór 9:00'"
                className="rounded-none mt-1 w-full min-w-0"
              />
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-gray-500 uppercase tracking-wide font-bold mr-1">Navigatie:</span>
              {["google", "waze", "apple"].map((k) => (
                preview.navigation_links?.[k] ? (
                  <Button key={k} variant="outline" size="sm" onClick={() => copyNavLink(k)} className="rounded-none text-xs" data-testid={`dispatch-copy-${k}`}>
                    <Copy className="w-3 h-3 mr-1" /> {NAV_LABELS[k]}
                  </Button>
                ) : null
              ))}
            </div>
          </>
        )}
        {loadingPreview && <div className="text-xs text-gray-500">Preview laden…</div>}

        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
          <Button
            onClick={onSendWhatsapp}
            disabled={!selectedDriverId || sending}
            className="rounded-none bg-[#25D366] hover:bg-[#1ebd5a] text-white font-chivo font-bold uppercase text-xs"
            data-testid="dispatch-send-whatsapp"
          >
            <MessageCircle className="w-4 h-4 mr-1" /> Stuur via WhatsApp
          </Button>
          <Button
            onClick={() => setEmailConfirmOpen(true)}
            disabled={!selectedDriverId || sending || !selectedDriver?.email}
            className="rounded-none bg-[#0055FF] hover:bg-[#003ecc] text-white font-chivo font-bold uppercase text-xs"
            data-testid="dispatch-send-email"
            title={!selectedDriver?.email ? "Chauffeur heeft geen e-mailadres" : "Stuur dispatch-mail"}
          >
            <Send className="w-4 h-4 mr-1" /> Stuur via e-mail
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-chivo font-bold text-sm text-[#0A192F] flex items-center gap-2">
            <History className="w-4 h-4" /> Dispatchgeschiedenis
          </h3>
        </div>
        {historyLoading ? (
          <div className="text-xs text-gray-500">Laden…</div>
        ) : history.length === 0 ? (
          <div className="text-xs text-gray-500 italic">Nog geen dispatches voor deze afspraak.</div>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-200" data-testid="dispatch-history-list">
            {history.map((h) => (
              <li key={h.id} className="p-3 text-xs flex flex-wrap items-center justify-between gap-2" data-testid={`dispatch-history-row-${h.id}`}>
                <div className="min-w-0">
                  <div className="font-chivo font-bold text-[#0A192F] flex items-center gap-2 flex-wrap">
                    {h.channel === "email" ? <Send className="w-3 h-3" /> : <MessageCircle className="w-3 h-3" />}
                    {h.channel === "email" ? "E-mail" : "WhatsApp-link"} · {h.driver_name || "—"}
                    <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-wide font-bold ${DISPATCH_STATUS_CLASSES[h.status] || "bg-gray-100 text-gray-700"}`}>{DISPATCH_STATUS_LABELS[h.status] || h.status}</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {(h.sent_at || h.created_at) ? new Date(h.sent_at || h.created_at).toLocaleString("nl-NL") : "—"} · door {h.sent_manual_by || h.sent_by || "—"}
                  </div>
                </div>
                {/* "Markeer als verzonden" — alleen tonen bij WhatsApp-link
                    dispatches die nog niet handmatig bevestigd zijn. Geen
                    e-mail, geen failed, geen reeds-sent_manual. */}
                {h.channel === "whatsapp_link" && h.status === "whatsapp_link_generated" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setMarkCandidate(h)}
                    className="rounded-none text-[10px] h-7"
                    data-testid={`dispatch-mark-sent-${h.id}`}
                  >
                    <CheckCheck className="w-3 h-3 mr-1" /> Markeer als verzonden
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={emailConfirmOpen} onOpenChange={setEmailConfirmOpen}>        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>E-mail verzenden naar chauffeur?</DialogTitle>
            <DialogDescription>
              Verstuur dispatch-mail naar <strong>{selectedDriver?.name}</strong> ({selectedDriver?.email}).
              Het bericht en de navigatielinks zoals hierboven getoond worden bijgevoegd.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailConfirmOpen(false)} className="rounded-none">Annuleren</Button>
            <Button onClick={onConfirmSendEmail} disabled={sending} className="rounded-none bg-[#0055FF] hover:bg-[#003ecc] text-white font-chivo font-bold uppercase text-xs" data-testid="dispatch-email-confirm">
              {sending ? "Verzenden…" : "Verzenden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P0 Fallback-modal — getoond wanneer popup-blocking de WhatsApp-tab
          tegenhoudt. Geeft drie ontsnappingsroutes: handmatig openen, link
          kopiëren, of berichttekst kopiëren. */}
      <Dialog open={!!fallback} onOpenChange={(o) => { if (!o) setFallback(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>WhatsApp kon niet automatisch openen</DialogTitle>
            <DialogDescription>
              Uw browser heeft het openen van WhatsApp geblokkeerd. Gebruik de knop
              hieronder of kopieer het bericht handmatig.
            </DialogDescription>
          </DialogHeader>
          {fallback && (
            <div className="space-y-3">
              <a
                href={fallback.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setTimeout(() => setFallback(null), 300)}
                data-testid="dispatch-fallback-open"
                className="block w-full text-center rounded-none bg-[#25D366] hover:bg-[#1ebd5a] text-white font-chivo font-bold uppercase text-xs px-4 py-2"
              >
                <ExternalLink className="w-4 h-4 mr-1 inline-block" /> Open WhatsApp
              </a>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => copyText(fallback.messageText, "Bericht")}
                  className="rounded-none w-full"
                  data-testid="dispatch-fallback-copy-message"
                >
                  <Copy className="w-3 h-3 mr-1" /> Kopieer bericht
                </Button>
                <Button
                  variant="outline"
                  onClick={() => copyText(fallback.url, "WhatsApp-link")}
                  className="rounded-none w-full"
                  data-testid="dispatch-fallback-copy-link"
                >
                  <Copy className="w-3 h-3 mr-1" /> Kopieer link
                </Button>
              </div>
              <div className="bg-gray-50 border border-gray-200 p-2 break-all text-[11px] font-mono text-gray-700" data-testid="dispatch-fallback-url">
                {fallback.url}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFallback(null)} className="rounded-none" data-testid="dispatch-fallback-close">Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P1 — Handmatige bevestiging dat de medewerker het bericht ook
          daadwerkelijk in WhatsApp heeft verstuurd. Voorkomt valse claim
          "verzonden" zonder WhatsApp Cloud API. */}
      <Dialog open={!!markCandidate} onOpenChange={(o) => { if (!o) setMarkCandidate(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>WhatsApp-bericht als verzonden markeren?</DialogTitle>
            <DialogDescription>
              Gebruik deze knop alleen als u het bericht daadwerkelijk in WhatsApp heeft verzonden.
            </DialogDescription>
          </DialogHeader>
          {markCandidate && (
            <div className="text-xs text-gray-600 space-y-0.5">
              <div><strong>Chauffeur:</strong> {markCandidate.driver_name}</div>
              <div><strong>Gegenereerd op:</strong> {new Date(markCandidate.created_at).toLocaleString("nl-NL")}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkCandidate(null)} className="rounded-none">Annuleren</Button>
            <Button
              onClick={onConfirmMarkSent}
              disabled={marking}
              className="rounded-none bg-green-700 hover:bg-green-800 text-white font-chivo font-bold uppercase text-xs"
              data-testid="dispatch-mark-sent-confirm"
            >
              {marking ? "Markeren…" : "Markeer als verzonden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
